import { pick } from "lodash-es";
import { storeToRefs } from "pinia";
import { computed, type Ref } from "vue";

import { outreachFeatures, socialFeatures } from "shared/boot/authorization";
import { getChartConfigs } from "shared/modules/Charts/helpers/charts";
import { type SavedChartSetting } from "shared/modules/Charts/types";
import { type ChartConfigIndex } from "shared/modules/Charts/types/chartConfig";
import type { InstantInsightsChart } from "shared/modules/Charts/types/chartTypes";
import type { AppConfig } from "shared/services/appConfig/types";
import features from "shared/services/features";
import { useGlobalStore } from "shared/stores/global";
import { useUserStore } from "shared/stores/user";
import type { Stream } from "shared/types";
import { type FeatureKey } from "shared/types/features";

interface InstantInsightsCharts {
  chartsIndex: Ref<ChartConfigIndex>;
  filterSelectedCharts(
    selectedCharts: SavedChartSetting[]
  ): SavedChartSetting[];
}

const getAppConfig = (): Ref<AppConfig> => {
  const globalStore = useGlobalStore();

  return storeToRefs(globalStore).appConfig as Ref<AppConfig>;
};

export function setupCharts({
  streamsData,
}: {
  streamsData?: Stream[];
}): ChartConfigIndex {
  return getChartConfigs({ streamsData });
}

export function filterChartsByFeatureAndPermission(
  charts: ChartConfigIndex
): ChartConfigIndex {
  const chartsIndex = {} as ChartConfigIndex;

  (Object.keys(charts) as InstantInsightsChart[]).forEach((chartKey) => {
    const chart = charts[chartKey]!;

    const hasFeature =
      !chart.featureFlag ||
      features.hasAny(chart.featureFlag.split(",") as FeatureKey[]);

    const hasPermissions =
      !chart.requiredPermissions ||
      chart.requiredPermissions.every(
        (permission) =>
          socialFeatures.hasSocialPermission(permission) ||
          outreachFeatures.hasOutreachPermission(permission)
      );

    const hasBetaAccess = !chart.isBeta || useUserStore().betaEnabled;

    if (hasFeature && hasPermissions && hasBetaAccess) {
      chartsIndex[chartKey] = chart;
    }
  });

  return chartsIndex;
}

interface InstantInsightsChartsOptions {
  streams?: Stream[];
}

export function useInstantInsightsCharts(
  options: InstantInsightsChartsOptions = {}
): InstantInsightsCharts {
  const { streams } = options;

  const appConfig = getAppConfig();

  const availableSocialCharts = appConfig.value.instantInsightsCharts;

  const availableCharts = pick(
    setupCharts({ streamsData: streams }),
    availableSocialCharts
  );

  const chartsIndex = computed(() =>
    filterChartsByFeatureAndPermission(availableCharts)
  );

  const filterSelectedCharts: InstantInsightsCharts["filterSelectedCharts"] = (
    selectedCharts
  ) => selectedCharts.filter(({ chart }) => chartsIndex.value[chart]);

  return {
    chartsIndex,
    filterSelectedCharts,
  };
}

export default useInstantInsightsCharts;
